/**
 * Workflow Purge Middleware
 *
 * This middleware provides targeted purging of workflow state from localStorage.
 * Unlike global persistor.purge(), this only removes the workflow encounter ID,
 * preserving other persisted state (actions, appointments, billing, etc.).
 *
 * The purge action is whitelisted in redux-state-sync to mirror across tabs.
 *
 * @module features/encounters/store/workflowPurgeMiddleware
 */

import type { Middleware } from '@reduxjs/toolkit';
import { getLegacyKey, getScopedKey, type Owner } from '@store/owner';

/**
 * Action type for targeted workflow purge
 */
export const WORKFLOW_PERSIST_CLEAR = 'encounters/workflow/PERSIST_CLEAR' as const;

/**
 * Workflow slice key used for storage
 */
const WORKFLOW_SLICE_KEY = 'feature_encounters_encounters/workflow';

/**
 * Action creator to purge workflow state
 *
 * @param owner - Current owner context
 * @returns Action that will be handled by middleware
 */
export function purgeWorkflowFor(owner: Owner) {
  return {
    type: WORKFLOW_PERSIST_CLEAR,
    meta: { owner },
  };
}

/**
 * Middleware that handles targeted workflow purge actions
 *
 * When WORKFLOW_PERSIST_CLEAR is dispatched:
 * 1. Removes scoped workflow key for current owner
 * 2. Removes legacy unscoped workflow key (if present)
 * 3. Does NOT touch any other persisted state
 *
 * This action is synced across tabs via redux-state-sync to ensure
 * consistent state across all open windows.
 */
export const workflowPurgeMiddleware: Middleware = () => next => (action: any) => {
  // Check if this is a workflow purge action
  if (action?.type === WORKFLOW_PERSIST_CLEAR) {
    const owner: Owner | undefined = action?.meta?.owner;

    if (owner) {
      try {
        // Remove scoped workflow key
        const scopedKey = getScopedKey(owner, WORKFLOW_SLICE_KEY);
        localStorage.removeItem(scopedKey);

        // Remove legacy unscoped key (for backward compatibility)
        const legacyKey = getLegacyKey(WORKFLOW_SLICE_KEY);
        localStorage.removeItem(legacyKey);

        if (process.env.NODE_ENV !== 'production') {
          console.log('[WorkflowPurge] Cleared workflow keys:', {
            scoped: scopedKey,
            legacy: legacyKey,
          });
        }
      } catch (error) {
        // Ignore localStorage errors (e.g., in private browsing mode)
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[WorkflowPurge] Failed to clear workflow keys:', error);
        }
      }
    }
  }

  return next(action);
};
